import { useRef, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Upload, X, CheckCircle2, Loader2, Sofa, Home, Leaf, Recycle } from "lucide-react";
import { formatApiErrorDetail } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: "meubels", label: "Meubels", icon: Sofa, from: "vanaf €49" },
  { id: "woningontruiming", label: "Woningontruiming", icon: Home, from: "vanaf €249" },
  { id: "tuinafval", label: "Tuinafval", icon: Leaf, from: "vanaf €79" },
  { id: "recyclage", label: "Recyclage", icon: Recycle, from: "vanaf €39" },
];

const inputCls =
  "w-full border border-white/15 bg-ink px-4 py-3 text-sm text-paper placeholder:text-muted2/60 outline-none transition-colors duration-200 focus:border-brand";

export default function QuoteForm() {
  const [category, setCategory] = useState("meubels");
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", description: "" });
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const active = CATEGORIES.find((c) => c.id === category);

  const pickPhoto = (file) => {
    if (!file) return;
    setPhoto(file);
    setPreview(URL.createObjectURL(file));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const data = new FormData();
      Object.entries({ ...form, category }).forEach(([k, v]) => data.append(k, v));
      if (photo) data.append("photo", photo);
      await axios.post(`${API}/quotes`, data, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
      toast.success("Aanvraag verzonden! Je ontvangt snel een offerte.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="offerte" className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28" data-testid="quote-section">
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5"
        >
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-brand">// Gratis offerte</p>
          <h2 className="mt-3 font-display text-4xl font-black uppercase tracking-tight text-paper sm:text-5xl lg:text-6xl">
            Foto erbij, <span className="text-brand">prijs erbij</span>
          </h2>
          <p className="mt-6 max-w-md text-base text-muted2">
            Vul je gegevens in en voeg een foto toe van wat weg moet. Wij bekijken het en sturen
            je een vaste prijs — daarna plannen we de ophaling.
          </p>
          <div className="mt-8 border border-safety/30 bg-safety/5 p-5" data-testid="price-hint">
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-safety">Indicatie voor jouw categorie</div>
            <div className="mt-1 font-display text-4xl font-black text-paper">{active.from}</div>
            <p className="mt-1 text-xs text-muted2">Exacte prijs volgt na beoordeling van je foto.</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="lg:col-span-7"
        >
          {done ? (
            <div className="flex h-full flex-col items-center justify-center border border-safety/40 bg-coal p-12 text-center" data-testid="quote-success">
              <CheckCircle2 className="h-14 w-14 text-safety" />
              <h3 className="mt-6 font-display text-3xl font-black uppercase text-paper">Aanvraag ontvangen!</h3>
              <p className="mt-3 max-w-sm text-sm text-muted2">
                Bedankt {form.name.split(" ")[0]}. Wij bekijken je foto en sturen zo snel mogelijk een offerte naar {form.email}.
              </p>
              <button
                onClick={() => { setDone(false); setForm({ name: "", email: "", phone: "", address: "", description: "" }); setPhoto(null); setPreview(null); }}
                className="mt-8 border border-white/20 px-6 py-3 font-display text-base font-bold uppercase text-paper transition-colors hover:border-brand hover:text-brand"
                data-testid="quote-new-request-btn"
              >
                Nieuwe aanvraag
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="border border-white/10 bg-coal p-6 sm:p-8" data-testid="quote-form">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="category-selector">
                {CATEGORIES.map((c) => (
                  <button
                    type="button"
                    key={c.id}
                    onClick={() => setCategory(c.id)}
                    data-testid={`category-${c.id}-btn`}
                    className={`flex flex-col items-center gap-2 border px-2 py-4 transition-colors duration-200 ${
                      category === c.id ? "border-brand bg-brand/10 text-brand" : "border-white/15 text-muted2 hover:border-white/40 hover:text-paper"
                    }`}
                  >
                    <c.icon className="h-5 w-5" />
                    <span className="text-center text-xs font-bold uppercase tracking-wide">{c.label}</span>
                  </button>
                ))}
              </div>
              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <input required value={form.name} onChange={set("name")} placeholder="Je naam *" className={inputCls} data-testid="quote-name-input" />
                <input required type="tel" value={form.phone} onChange={set("phone")} placeholder="Telefoonnummer *" className={inputCls} data-testid="quote-phone-input" />
                <input required type="email" value={form.email} onChange={set("email")} placeholder="E-mailadres *" className={inputCls} data-testid="quote-email-input" />
                <input required value={form.address} onChange={set("address")} placeholder="Adres van ophaling *" className={inputCls} data-testid="quote-address-input" />
              </div>
              <textarea
                value={form.description}
                onChange={set("description")}
                placeholder="Beschrijf kort wat weg moet (aantal, verdieping, lift aanwezig...)"
                rows={3}
                className={`${inputCls} mt-4 resize-none`}
                data-testid="quote-description-input"
              />
              <div className="mt-4">
                {preview ? (
                  <div className="flex items-center gap-4 border border-white/15 bg-ink p-3" data-testid="photo-preview">
                    <img src={preview} alt="Geüploade foto" className="h-20 w-20 border border-white/10 object-cover" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm text-paper">{photo?.name}</p>
                      <p className="text-xs text-muted2">Foto toegevoegd</p>
                    </div>
                    <button type="button" onClick={() => { setPhoto(null); setPreview(null); }} className="text-muted2 hover:text-brand" data-testid="photo-remove-btn" aria-label="Foto verwijderen">
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="flex w-full items-center justify-center gap-3 border border-dashed border-white/25 bg-ink px-4 py-8 text-muted2 transition-colors duration-200 hover:border-brand hover:text-brand"
                    data-testid="photo-upload-btn"
                  >
                    <Upload className="h-5 w-5" />
                    <span className="text-sm">Voeg een foto toe van wat weg moet (optioneel, maar handig)</span>
                  </button>
                )}
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => pickPhoto(e.target.files?.[0])} data-testid="photo-file-input" />
              </div>
              <button
                type="submit"
                disabled={sending}
                className="mt-6 flex w-full items-center justify-center gap-3 bg-brand px-8 py-4 font-display text-xl font-bold uppercase tracking-wide text-ink transition-colors duration-200 hover:bg-safety disabled:opacity-60"
                data-testid="quote-submit-btn"
              >
                {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
                {sending ? "Versturen..." : "Verstuur aanvraag — gratis & vrijblijvend"}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
